import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, ExternalLink, Trash2, Flame } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";

const SwipeFile = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/swipe-file");
      setItems(data.items || []);
    } catch (e) {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (videoId) => {
    try {
      await api.delete(`/swipe-file/${videoId}`);
      setItems((s) => s.filter((i) => i.video_id !== videoId));
      toast.success("Removed from swipe file");
    } catch (e) { toast.error("Failed to remove"); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Swipe File"
        subtitle="Your personal library of viral inspiration."
        testId="swipe-header"
        actions={<Link to="/dashboard" className="op-btn-secondary text-sm py-2"><Flame size={14} className="text-orange-500" /> Browse Viral</Link>}
      />
      {loading ? (
        <div className="op-card p-8 text-center text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="op-card">
          <EmptyState
            icon={Bookmark}
            title="Nothing saved yet"
            description="Tap the bookmark icon on any viral video to save it here."
            action={<Link to="/dashboard" className="op-btn-primary">Browse Viral Picks →</Link>}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="swipe-grid">
          {items.map((t) => (
            <div key={t.video_id} className="op-card overflow-hidden" data-testid={`swipe-${t.video_id}`}>
              <div className="aspect-video bg-black relative">
                {t.embed_url && (
                  <iframe
                    src={t.embed_url}
                    className="absolute inset-0 w-full h-full"
                    title={t.title}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
                {t.industry && (
                  <span className="absolute top-2 left-2 px-2.5 py-1 bg-[#7C3AED] text-white text-[10px] font-bold rounded-full uppercase tracking-wider z-10 pointer-events-none">{t.industry}</span>
                )}
              </div>
              <div className="p-4">
                <p className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">{t.title}</p>
                <p className="text-xs text-gray-500 mb-2">{t.channel}{t.year ? ` · ${t.year}` : ""}{t.views_label ? ` · ${t.views_label}` : ""}</p>
                {t.why_viral && <p className="text-xs text-gray-600 italic line-clamp-2 mb-3">"{t.why_viral}"</p>}
                <div className="flex items-center justify-between">
                  <a href={t.watch_url} target="_blank" rel="noreferrer" className="text-xs text-[#7C3AED] font-semibold inline-flex items-center gap-1 hover:underline">
                    Watch on YouTube <ExternalLink size={12} />
                  </a>
                  <button onClick={() => remove(t.video_id)} className="text-xs text-red-500 font-medium inline-flex items-center gap-1 hover:underline" data-testid={`remove-${t.video_id}`}>
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </PageContainer>
  );
};

export default SwipeFile;
